import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from '../../context/LocationContext';
import { loadGoogleMapsScript, isGoogleMapsLoaded } from '../../utils/mapLoader';
import { toast } from 'react-toastify';

const LocationDrawer = ({ 
  isOpen,
  onClose,
  onLocationSelect, 
  placeholder = "Search for area, street name...",
  initialLocation = null
}) => {
  const { getCurrentLocation } = useLocation();
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isLoadingMaps, setIsLoadingMaps] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Refs
  const inputRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);

  // Initialize Google Maps when component mounts
  useEffect(() => {
    if (isOpen) {
      loadGoogleMapsAPI();
      // Focus input when drawer opens
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 300);
    }
  }, [isOpen]);

  // Set initial location if provided
  useEffect(() => {
    if (initialLocation) {
      setSelectedLocation(initialLocation);
      if (initialLocation.cityName) {
        setSearchValue(initialLocation.cityName);
      } else if (initialLocation.address) {
        const cityName = extractCityFromAddress(initialLocation.address);
        setSearchValue(cityName);
      }
    }
  }, [initialLocation]);

  const loadGoogleMapsAPI = async () => {
    setIsLoadingMaps(true);
    try {
      await loadGoogleMapsScript(['places']);
      initializeServices();
    } catch (error) {
      console.error('Error loading Google Maps:', error);
      toast.error('Error loading Google Maps. Some features may not work.');
    } finally {
      setIsLoadingMaps(false);
    }
  };

  const initializeServices = () => {
    if (!isGoogleMapsLoaded()) return;

    try {
      // Initialize AutocompleteService for suggestions
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      
      // Create a dummy div for PlacesService (required by Google Maps)
      const dummyDiv = document.createElement('div');
      placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);
    } catch (error) {
      console.error('Error initializing Google Places services:', error);
    }
  };

  const extractCityFromAddress = (address) => {
    if (!address) return '';
    
    // Split by commas and take the first part as city
    const parts = address.split(',');
    if (parts.length > 0) {
      return parts[0].trim();
    }
    
    return address;
  };

  const handleAutocompleteSearch = (query) => {
    if (!query.trim() || !autocompleteServiceRef.current) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const request = {
      input: query,
      types: ['(cities)'],
      componentRestrictions: { country: 'in' }
    };

    autocompleteServiceRef.current.getPlacePredictions(request, (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        setSuggestions(predictions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    });
  };

  const handleSuggestionSelect = (suggestion) => {
    if (!placesServiceRef.current) return;

    const request = {
      placeId: suggestion.place_id,
      fields: ['geometry', 'formatted_address', 'address_components', 'name']
    };

    placesServiceRef.current.getDetails(request, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
        const location = place.geometry.location;
        
        // Extract city name from place details
        let cityName = '';
        let state = '';
        let country = '';
        
        if (place.address_components) {
          place.address_components.forEach(component => {
            const types = component.types;
            if (types.includes('locality')) {
              cityName = component.long_name;
            } else if (types.includes('administrative_area_level_2') && !cityName) {
              cityName = component.long_name;
            } else if (types.includes('administrative_area_level_1')) {
              state = component.long_name;
            } else if (types.includes('country')) {
              country = component.long_name;
            }
          });
        }
        
        // Fallback to place name if no city found
        if (!cityName) {
          cityName = place.name || extractCityFromAddress(place.formatted_address);
        }

        const locationData = {
          latitude: location.lat(),
          longitude: location.lng(),
          address: place.formatted_address,
          cityName: cityName,
          city: cityName,
          state: state,
          country: country,
          placeId: suggestion.place_id,
          pincode: ''
        };

        setSelectedLocation(locationData);
        setSearchValue(cityName);
        setShowSuggestions(false);

        if (onLocationSelect) {
          onLocationSelect(locationData);
        }

        toast.success('Location selected successfully!');
        
        // Close drawer after selection
        setTimeout(() => {
          onClose();
        }, 100);
      }
    });
  };

  const handleCurrentLocation = async () => {
    setIsLoadingLocation(true);
    
    try {
      await getCurrentLocation();
      toast.success('Current location detected successfully!');
      
      // Close drawer after selection
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (error) {
      console.error('Error getting location:', error);
      toast.error('Unable to get your current location. Please check permissions.');
    } finally {
      setIsLoadingLocation(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      handleAutocompleteSearch(value);
    }, 300);
  };

  const handleInputFocus = () => {
    if (searchValue && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow for selection
    setTimeout(() => {
      setShowSuggestions(false);
    }, 150);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Centering wrapper — min-h-full + flex guarantees true vertical center */}
      <div className="flex min-h-full items-center justify-center p-4">
      {/* Modal card */}
      <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl animate-[fadeInScale_0.2s_ease-out]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center space-x-2">
            <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h2 className="text-base font-semibold text-gray-900">Select Location</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search Input */}
        <div className="px-5 pt-4 pb-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder={placeholder}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              autoComplete="off"
            />
          </div>

          {/* Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-52 overflow-y-auto">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.place_id}
                  onClick={() => handleSuggestionSelect(suggestion)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center space-x-3"
                >
                  <svg className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {suggestion.structured_formatting?.main_text || suggestion.description}
                    </div>
                    {suggestion.structured_formatting?.secondary_text && (
                      <div className="text-xs text-gray-500 truncate">
                        {suggestion.structured_formatting.secondary_text}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Divider with label */}
        <div className="flex items-center px-5 pb-3">
          <div className="flex-1 h-px bg-gray-100" />
          <span className="px-3 text-xs text-gray-400">or</span>
          <div className="flex-1 h-px bg-gray-100" />
        </div>

        {/* Current Location Option */}
        <div className="px-5 pb-5">
          <button
            onClick={handleCurrentLocation}
            disabled={isLoadingLocation}
            className="w-full flex items-center px-4 py-3 rounded-xl border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors"
          >
            <div className="flex-shrink-0 mr-3">
              {isLoadingLocation ? (
                <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
              ) : (
                <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </div>
            <div className="flex-1 text-left">
              <div className="text-sm font-medium text-primary">Use current location</div>
              <div className="text-xs text-gray-500">Detect via GPS</div>
            </div>
          </button>
        </div>
      </div>
      </div>
    </div>,
    document.body
  );
};

export default LocationDrawer; 